import React, { useState } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { X, Radio, Satellite } from 'lucide-react';
import { gpsImportEndpoints } from '../../../endpoints/sessions';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomSelectInput from '../../../shared/CustomSelectInput/CustomSelectInput';
import { FileDropZone } from './csv-wizard/FileDropZone';

interface GpsUploadDialogProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
  rosterAthletes: { _id: string; firstName: string; lastName: string }[];
  onCompleted?: () => void;
}

const SPORT_OPTIONS = [
  { value: '', label: 'Auto-detect dal file' },
  { value: 'football', label: 'Calcio' },
  { value: 'rugby', label: 'Rugby' },
  { value: 'basketball', label: 'Basket' },
  { value: 'volleyball', label: 'Pallavolo' },
  { value: 'running', label: 'Running' },
  { value: 'cycling', label: 'Ciclismo' },
];

/**
 * Modale upload file GPS singolo (.fit/.gpx) per atleta singolo.
 * Backend parser: fit-file-parser per .fit, gpxparser per .gpx.
 * POST /sessions/import → crea Session con commonMetrics+enduranceMetrics
 * popolate da telemetria GPS reale + audit SESSION_IMPORT_*.
 */
export const GpsUploadDialog: React.FC<GpsUploadDialogProps> = ({
  open,
  onClose,
  teamId,
  rosterAthletes,
  onCompleted,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [athleteId, setAthleteId] = useState<string>('');
  const [sportOverride, setSportOverride] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    success?: boolean;
    error?: string;
    sessionId?: string;
    duration?: number;
    td?: number;
  } | null>(null);

  if (!open) return null;

  const fileToBase64 = (f: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // strip "data:*/*;base64," prefix
        const b64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(b64);
      };
      reader.onerror = () => reject(new Error('Errore lettura file'));
      reader.readAsDataURL(f);
    });

  const handleSubmit = async () => {
    if (!file || !athleteId) {
      setResult({ error: 'Seleziona file + atleta' });
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const fileBase64 = await fileToBase64(file);
      const res = await gpsImportEndpoints.importFile({
        filename: file.name,
        fileBase64,
        athleteId,
        teamId,
        sportOverride: sportOverride || undefined,
      });
      setResult({
        success: true,
        sessionId: res.data.sessionId,
        duration: res.data.duration,
        td: res.data.td,
      });
      onCompleted?.();
    } catch (e: any) {
      setResult({
        error: e?.response?.data?.message ?? e?.message ?? 'Errore upload',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setAthleteId('');
    setSportOverride('');
    setResult(null);
    onClose();
  };

  const athleteOptions = [
    { value: '', label: '— seleziona atleta —' },
    ...rosterAthletes.map((a) => ({
      value: a._id,
      label: `${a.firstName} ${a.lastName}`,
    })),
  ];

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.500"
      zIndex={1050}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={handleClose}
    >
      <Box
        bg="var(--c-card-bg)"
        borderRadius="lg"
        p={6}
        maxWidth="560px"
        width="92%"
        onClick={(e) => e.stopPropagation()}
      >
        <HStack justifyContent="space-between" mb={4}>
          <Box>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">
              Upload GPS
            </Text>
            <Text fontSize="xl" fontWeight="700">
              Importa file .fit / .gpx
            </Text>
          </Box>
          <CustomButton size="sm" color="text" onClick={handleClose}>
            <X size={14} />
          </CustomButton>
        </HStack>

        <VStack alignItems="stretch" gap={4}>
          {/* Atleta selector */}
          <Box>
            <Text fontSize="sm" fontWeight={600} mb={1}>
              Atleta *
            </Text>
            <CustomSelectInput
              options={athleteOptions}
              value={athleteId}
              onChange={(val: string) => setAthleteId(val)}
              placeholder="— seleziona atleta —"
              menuPosition="fixed"
            />
          </Box>

          {/* Sport override */}
          <Box>
            <Text fontSize="sm" fontWeight={600} mb={1}>
              Sport
            </Text>
            <CustomSelectInput
              options={SPORT_OPTIONS}
              value={sportOverride}
              onChange={(val: string) => setSportOverride(val)}
              placeholder="Auto-detect dal file"
              menuPosition="fixed"
            />
          </Box>

          {/* File picker — drag-drop zone shared */}
          <Box>
            <Text fontSize="sm" fontWeight={600} mb={2}>
              File GPS *
            </Text>
            <FileDropZone
              accept=".fit,.gpx"
              maxSizeMB={10}
              icon={<Satellite size={28} />}
              title="Trascina file .fit o .gpx, o clicca per selezionare"
              subtitle="Formati: .fit (Garmin/Wahoo binary) · .gpx (XML standard)"
              selectedFile={file}
              onFileSelected={(f) => {
                setFile(f);
                setResult(null);
              }}
            />
          </Box>

          {/* Result */}
          {result?.error && (
            <Box bg="red.50" p={3} borderRadius="md" fontSize="sm" color="red.700" display="flex" alignItems="flex-start" gap={2}>
              <X size={14} style={{ flexShrink: 0, marginTop: 2 }} /> {result.error}
            </Box>
          )}
          {result?.success && (
            <Box bg="green.50" p={3} borderRadius="md" fontSize="sm" color="green.800">
              ✓ Sessione creata · ID {result.sessionId?.slice(-6)} · {result.duration}min · TD {result.td?.toLocaleString('it-IT')}m
            </Box>
          )}
        </VStack>

        <HStack justifyContent="flex-end" gap={2} mt={6}>
          <CustomButton size="sm" color="outline" onClick={handleClose}>
            {result?.success ? 'Chiudi' : 'Annulla'}
          </CustomButton>
          {!result?.success && (
            <CustomButton
              size="sm"
              color="primary"
              onClick={handleSubmit}
              disabled={!file || !athleteId || submitting}
              isLoading={submitting}
              icon={<Radio size={12} />}
              iconPosition="left"
            >
              Importa GPS
            </CustomButton>
          )}
        </HStack>
      </Box>
    </Box>
  );
};
